import React, { Fragment } from 'react';
import { useDispatch } from 'react-redux';
import { getDNP3Data } from '../../redux/actions/otActions';
import { ErrorCodes } from '../../utils/errorCodes';
import { formatFrontendSupportError } from '../../utils/frontendErrorFormat';

import Content from '../../components/Content';
import OTDataTable from '../../components/OTDataTable';
import Loader from '../../components/Loader';
import NetworkError from '../../components/Error';
import DNP3Styles from './DNP3.module.css';

const FRONTEND_DNP3_ERROR_CODES = {
	INITIAL_LOAD_FAILED: ErrorCodes.API_INTERNAL_ERROR,
};

const buildDNP3SupportError = (errorCode, pageInfo, sourceMessage) =>
	formatFrontendSupportError({ errorCode, pageInfo, sourceMessage });

const DNP3Screen = () => {
	const dispatch = useDispatch();

	const [ dataLoading, setDataLoading ] = React.useState(false);
	const [ dNP3Data, setDNP3Data ] = React.useState({});
	const [ supportErrorMessage, setSupportErrorMessage ] = React.useState('');
	const [ copiedSupportError, setCopiedSupportError ] = React.useState(false);

	const setDNP3SupportError = React.useCallback((errorCode, pageInfo, sourceMessage) => {
		setSupportErrorMessage(buildDNP3SupportError(errorCode, pageInfo, sourceMessage));
		setCopiedSupportError(false);
	}, []);

	const clearDNP3SupportError = React.useCallback(() => {
		setSupportErrorMessage('');
		setCopiedSupportError(false);
	}, []);

	const handleCopySupportError = React.useCallback(async () => {
		if (!supportErrorMessage) return;
		try {
			await navigator.clipboard.writeText(supportErrorMessage);
			setCopiedSupportError(true);
			setTimeout(() => setCopiedSupportError(false), 2000);
		} catch (error) {
			setCopiedSupportError(false);
		}
	}, [ supportErrorMessage ]);

	React.useEffect(
		() => {
			const getData = async () => {
				setDataLoading(true);
				clearDNP3SupportError();
				try {
					const data = await dispatch(getDNP3Data());
					if (data && data.status && Array.isArray(data.DNP3RequestCols) && Array.isArray(data.DNP3RequestRows)) {
						setDNP3Data(data);
						clearDNP3SupportError();
					} else {
						setDNP3Data({});
						setDNP3SupportError(
							FRONTEND_DNP3_ERROR_CODES.INITIAL_LOAD_FAILED,
							'DNP3 Page / Initial Data Load Failed',
							data?.msg || (typeof data === 'string' ? data : 'DNP3 request did not return success')
						);
					}
				} catch (error) {
					setDNP3Data({});
					setDNP3SupportError(
						FRONTEND_DNP3_ERROR_CODES.INITIAL_LOAD_FAILED,
						'DNP3 Page / Initial Data Request Error',
						error?.message || String(error)
					);
				} finally {
					setDataLoading(false);
				}
			};

			getData();
		},
		[ dispatch, clearDNP3SupportError, setDNP3SupportError ]
	);

	return (
		<Content currentMenu='OT' headerTitle='DNP3 Data'>
			{dataLoading ? (
				<Loader />
			) : dNP3Data && Object.keys(dNP3Data).length === 0 ? (
				<div>
					<NetworkError />
					{supportErrorMessage && (
						<div className={DNP3Styles.supportErrorCard}>
							<strong>Support Error:</strong>
							<code className={DNP3Styles.supportErrorCode}>{supportErrorMessage}</code>
							<button
								type='button'
								className={DNP3Styles.copySupportErrorButton}
								onClick={handleCopySupportError}
							>
								{copiedSupportError ? 'Copied' : 'Copy Error Details'}
							</button>
						</div>
					)}
				</div>
			) : (
				<Fragment>
					{supportErrorMessage && (
						<div className={DNP3Styles.supportErrorBanner}>
							<div className={DNP3Styles.supportErrorBannerText}>
								<strong>Support Error:</strong>
								<code className={DNP3Styles.supportErrorCode}>{supportErrorMessage}</code>
							</div>
							<button
								type='button'
								className={DNP3Styles.copySupportErrorButton}
								onClick={handleCopySupportError}
							>
								{copiedSupportError ? 'Copied' : 'Copy Error Details'}
							</button>
						</div>
					)}
					<OTDataTable
						cols={dNP3Data.DNP3RequestCols}
						rows={dNP3Data.DNP3RequestRows}
						containerStyle={{ height: '75vh' }}
					/>
				</Fragment>
			)}
		</Content>
	);
};

export default DNP3Screen;
